import React, { useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { FolderStatus } from '@alucio/aws-beacon-amplify/src/models';
import {
  DNABox,
  DNACard,
  DNAButton,
  DNAText,
  Iffy,
  DNAIcon,
} from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import {
  DNAFolderStatusChip,
  DNAFolderPinButton,
  DNAFolderContextMenu,
  useDNAFolderActions,
  useCurrentPage,
} from 'src/components/DNA';
import { FolderORM } from 'src/types/types';
import { useAppSettings } from 'src/state/context/AppSettings';
import { DNACardComponent } from 'src/components/DNA/Grid/Cards/types';
import CARD_CONSTANTS, { GRID_CARD_STYLES_VARIANTS_CONFIGS } from './constants';

const styles = StyleSheet.create({
  folderCardContainer: {
    paddingHorizontal: 8,
    paddingTop: 8,
    paddingBottom: 16,
    minWidth: CARD_CONSTANTS.DEFAULT_CARD_WIDTH_DESKTOP,
    maxWidth: CARD_CONSTANTS.DEFAULT_CARD_WIDTH_DESKTOP,
    minHeight: CARD_CONSTANTS.DEFAULT_CARD_HEIGHT_DESKTOP,
    maxHeight: CARD_CONSTANTS.DEFAULT_CARD_HEIGHT_DESKTOP,
  },
  folderIconContainer: {
    paddingTop: 40,
  },
  folderName: {
    textAlign: 'center',
    maxWidth: 250,
    color: colors['color-text-light-primary'],
  },
  ownerName: {
    color: colors['color-text-light-tertiary'],
  },
})

export const DNAFolderDefaultCard: DNACardComponent<FolderORM> = (props) => {
  const { ORM: folderORM, onPress } = props;
  const { deviceMode } = useAppSettings();
  const folderActions = useDNAFolderActions();
  const route = useCurrentPage({ exact: false });
  const isDesktop = deviceMode === 'desktop';
  const isSharedFolders = route?.PATH.includes('shared_folders');
  const isArchivedFolders = route?.PATH.includes('archived_folders');
  const isRootFolder = folderORM.relations.parentFolderORM === null;
  const ownerORM = folderORM.relations.ownerORM;
  const ownerName = `${ownerORM?.model.givenName} ${ownerORM?.model.familyName}`;
  const displaySharedFolderOwnerName = isSharedFolders && isRootFolder;
  const isPinned = folderORM.meta.isFavorite;
  const hideEllipsis = folderORM.model.status === FolderStatus.ARCHIVED && folderORM.meta.isSharedWithTheUser;
  const iconColor = useMemo(() => isArchivedFolders
    ? colors['color-ui2-neutral-400']
    : colors['color-ui2-brand-200'], [isArchivedFolders]);

  return (
    <DNACard
      testID="folder-card-container"
      appearance="outline"
      interactive="pressable"
      onPress={() => onPress?.(folderORM)}
      style={styles.folderCardContainer}
    >
      {/* ACTION BUTTONS */}
      <DNABox spacing="between">
        <DNABox>
          <DNAFolderPinButton
            onPin={isPinned ? folderActions.unpin(folderORM) : folderActions.pin(folderORM)}
            isPinned={isPinned}
            isHidden={!isRootFolder}
          />
        </DNABox>
        <DNABox>
          <Iffy is={!hideEllipsis}>
            <DNAFolderContextMenu folderORM={folderORM}>
              <DNAButton
                appearance="ghost"
                status="subtle"
                size={isDesktop ? 'md' : 'xl'}
                iconLeft="dots-three-vertical-bold"
              />
            </DNAFolderContextMenu>
          </Iffy>
        </DNABox>
      </DNABox>
      {/* FOLDER ICON & NAME */}
      <DNABox fill appearance="col" alignX="center" spacing="md" style={styles.folderIconContainer}>
        <DNAIcon.Styled
          name="folder-simple-fill"
          color={iconColor}
          width={GRID_CARD_STYLES_VARIANTS_CONFIGS.FOLDER_ICON[deviceMode].width}
          height={GRID_CARD_STYLES_VARIANTS_CONFIGS.FOLDER_ICON[deviceMode].height}
        />
        <DNAText testID="folder-item-title" b1 numberOfLines={2} ellipsizeMode="tail" style={styles.folderName}>
          {folderORM.model.name}
        </DNAText>
      </DNABox>
      {/* FOLDER STATUS CHIP & FOLDER OWNER */}
      <DNABox fill alignX="center" alignY="end">
        <Iffy is={displaySharedFolderOwnerName}>
          <DNAText testID="folder-item-row-owner" style={styles.ownerName}>{ownerName}</DNAText>
        </Iffy>
        <Iffy is={!displaySharedFolderOwnerName}>
          <DNAFolderStatusChip orm={folderORM} />
        </Iffy>
      </DNABox>
    </DNACard>
  );
}

export default DNAFolderDefaultCard
